import React, { useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, AlertCircle, CalendarDays, Lock, ArrowRight, ChevronDown, Circle, X } from 'lucide-react';
import { Button } from '../new-components/ui/button';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { CONTENT_WIDTH } from '../new-components/layout.constants';
import type {
  AccountSetupBannerProps,
  OnboardingStep,
} from './AccountSetupBanner.types';
import { STEP_ICONS } from './AccountSetupBanner.types';

/**
 * AccountSetupBanner - Production-grade onboarding progress banner
 *
 * Design principles:
 * 1. Backend is the single source of truth for all business logic
 * 2. Component is a pure presentation layer
 * 3. Gracefully handles loading, error, and empty states
 * 4. Memoized to prevent unnecessary re-renders
 * 5. Fully accessible with ARIA attributes
 *
 * @example
 * ```tsx
 * const { data: onboarding, error, refetch } = useOnboardingStatus();
 *
 * <AccountSetupBanner
 *   onboarding={onboarding}
 *   error={error?.message}
 *   onStepClick={(stepId) => navigate(`/onboarding/${stepId}`)}
 *   onRetry={refetch}
 * />
 * ```
 */
export const AccountSetupBanner = React.memo<AccountSetupBannerProps>(
  function AccountSetupBanner({ onboarding, error, onStepClick, onRetry, stickyCondensed = true, onCondensedHeight, headerHeight, dismissKey = 'onboardingSuccessDismissed' }) {
    // Combine completed and remaining steps, sort by order
    // Hook must be called unconditionally before any early returns
    const allSteps = useMemo(() => {
      if (!onboarding) return [];

      const completed = onboarding.completedSteps.map((id) => ({
        id,
        completed: true,
        isAccessible: true, // Completed steps are always accessible
      }));

      const remaining = onboarding.remainingSteps;

      // Create a map for quick lookup
      const stepMap = new Map<string, OnboardingStep>();

      remaining.forEach((step) => {
        stepMap.set(step.id, step);
      });

      // For completed steps, we need to find their metadata from somewhere
      // In practice, backend should return full metadata for all steps
      // For now, we'll create minimal objects
      completed.forEach((stub) => {
        if (!stepMap.has(stub.id)) {
          // This is a completed step not in remainingSteps
          // We'll use placeholder metadata - in prod, backend should provide this
          stepMap.set(stub.id, {
            id: stub.id,
            title: formatStepTitle(stub.id),
            description: '',
            completed: true,
            isAccessible: true,
            order: -1, // Will be sorted out
          });
        }
      });

      return Array.from(stepMap.values()).sort((a, b) => a.order - b.order);
    }, [onboarding]);

    // Condensed sticky bar: appears once the full banner has scrolled up under the
    // sticky page header. The header height comes from the parent (single source of truth —
    // it already measures it for the nav/filter offsets), so the condensed bar's `top` can
    // never drift from the parent's pinned content. Fall back to a sane default standalone.
    const bannerRef = useRef<HTMLDivElement>(null);
    const headerH = headerHeight ?? 72;
    const [showCondensed, setShowCondensed] = useState(false);
    // Mobile-only collapse: the banner is collapsed by default and expands via the chevron.
    // On desktop (md+) it is always fully shown, so this state is inert there.
    const [collapsed, setCollapsed] = useState(true);
    // The scroll-in condensed bar is desktop-only; on mobile the collapsible banner replaces it
    // (which also removes the mobile scroll "twitch"). `md`+ mirrors Tailwind's `md:` classes.
    const isDesktop = useIsDesktop();
    const condensedEnabled = stickyCondensed && isDesktop;

    // Success bar (all steps complete) is dismissible; persists via localStorage under the
    // caller-supplied (per-user) key so it isolates between accounts on a shared browser.
    const [successDismissed, setSuccessDismissed] = useState(() => {
      try { return localStorage.getItem(dismissKey) === 'true'; } catch { return false; }
    });
    const handleDismissSuccess = () => {
      try { localStorage.setItem(dismissKey, 'true'); } catch { /* ignore */ }
      setSuccessDismissed(true);
    };
    // Report the condensed bar's *effective* height up so callers can pin content below it.
    // Measured height while showing, 0 while hidden — so the pinned filter bar's offset tracks
    // the bar appearing/disappearing (no enter/exit animation).
    const condensedRef = useRef<HTMLDivElement>(null);
    // Held in a ref so the report effect fires only on the meaningful triggers (show/mount),
    // not on every render when a caller passes an inline (unstable) callback.
    const onCondensedHeightRef = useRef(onCondensedHeight);
    onCondensedHeightRef.current = onCondensedHeight;
    // Layout effect (not useEffect): report the height and update the filter offset in the SAME
    // frame the condensed bar mounts/unmounts, so the sticky filter bar never lags a frame behind
    // (which would flash the cards through the gap when scrolling quickly).
    useLayoutEffect(() => {
      const report = () => {
        if (showCondensed && condensedRef.current) {
          onCondensedHeightRef.current?.(condensedRef.current.getBoundingClientRect().height);
        } else {
          onCondensedHeightRef.current?.(0);
        }
      };
      report();
      // While the bar is visible its height can change on resize/rotation (e.g. text wrapping
      // at a narrow width), so re-report — otherwise the caller's pinned offset goes stale.
      if (!showCondensed) return;
      window.addEventListener('resize', report);
      return () => window.removeEventListener('resize', report);
    }, [showCondensed]);
    useEffect(() => {
      if (!condensedEnabled) { setShowCondensed(false); return; }
      const el = bannerRef.current;
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => setShowCondensed(!entry.isIntersecting),
        { rootMargin: `-${Math.round(headerH)}px 0px 0px 0px`, threshold: 0 },
      );
      obs.observe(el);
      return () => obs.disconnect();
    }, [headerH, allSteps.length, condensedEnabled]);

    // Error state - show error banner with retry
    if (error) {
      return (
        <ErrorBanner
          message={error}
          onRetry={onRetry}
        />
      );
    }

    // Loading state - show skeleton
    if (!onboarding) {
      return <LoadingSkeleton />;
    }

    // Don't render if backend says to hide the banner
    if (!onboarding.shouldShowOnboardingBanner) {
      return null;
    }

    // Backend returned data but no steps - don't render
    if (onboarding.totalSteps === 0) {
      return null;
    }

    const showScheduleCTA = allSteps.some(
      (s) => s.id === 'schedule-meeting' && s.isAccessible && !s.completed
    );

    // Next actionable step + progress — drive the condensed sticky bar.
    const completedCount = allSteps.filter((s) => s.completed).length;
    const nextStep =
      allSteps.find((s) => s.isAccessible && !s.completed) ||
      allSteps.find((s) => !s.completed) ||
      null;

    // All steps complete → compact success state (dismissible), instead of the step grid.
    const isComplete = allSteps.length > 0 && completedCount === allSteps.length;
    if (isComplete) {
      if (successDismissed) return null;
      const ctaClass = 'items-center justify-center gap-2 rounded-xl bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition-colors hover:brightness-90 whitespace-nowrap';
      return (
        <div className="bg-secondary border border-border rounded-2xl p-3 rise-in">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full border-2 border-transparent bg-[hsl(var(--success))]">
                <Check className="h-4 w-4 text-background" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h3 className="font-medium leading-tight text-foreground">You're all set</h3>
                <p className="text-sm leading-tight text-muted-foreground">
                  You're ready to invest · {allSteps.length} of {allSteps.length} complete
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <Link to="/portal/invest/properties" className={`hidden md:inline-flex ${ctaClass}`}>
                Browse investments
              </Link>
              <button
                onClick={handleDismissSuccess}
                aria-label="Dismiss"
                className="-mr-1 p-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-border">
            <div className="h-full rounded-full bg-[hsl(var(--success))]" style={{ width: '100%' }} />
          </div>
          <Link to="/portal/invest/properties" className={`mt-3 flex md:hidden ${ctaClass}`}>
            Browse investments
          </Link>
        </div>
      );
    }

    // Mobile only: the header area (everything above the steps) toggles the banner open/closed
    // both ways. The step cards keep their own taps; desktop is always expanded.
    const headerIsToggle = !isDesktop;

    return (
      <>
      <div ref={bannerRef} className="bg-secondary border border-border rounded-2xl p-3">
        {/* Header + progress = the toggle target on mobile (everything above the steps). The
            step cards below stay their own tap targets. Desktop: plain (always expanded). */}
        <div
          className={headerIsToggle ? 'cursor-pointer' : undefined}
          role={headerIsToggle ? 'button' : undefined}
          tabIndex={headerIsToggle ? 0 : undefined}
          aria-expanded={headerIsToggle ? !collapsed : undefined}
          aria-label={
            headerIsToggle
              ? collapsed
                ? `Show setup steps, ${completedCount} of ${allSteps.length} complete`
                : 'Hide setup steps'
              : undefined
          }
          onClick={headerIsToggle ? () => setCollapsed((c) => !c) : undefined}
          onKeyDown={
            headerIsToggle
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setCollapsed((c) => !c);
                  }
                }
              : undefined
          }
        >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="min-w-0">
            <h3 className="font-medium text-lg text-foreground mb-0.5">Get ready to invest</h3>
            <p className="text-sm text-muted-foreground">
              {completedCount} of {allSteps.length} complete
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showScheduleCTA && (
              <button
                onClick={() => onStepClick('schedule-meeting')}
                className="hidden md:flex items-center gap-2 px-4 py-2.5 bg-foreground hover:brightness-90 text-background font-semibold rounded-xl transition-colors text-sm whitespace-nowrap"
              >
                <CalendarDays className="w-4 h-4" />
                Schedule a Meeting
              </button>
            )}
            {/* Mobile-only chevron — decorative; the header area is the actual toggle. Rotates
                to indicate open/closed. Desktop: hidden (always expanded). */}
            <span className="md:hidden p-1 -mr-1 text-muted-foreground" aria-hidden="true">
              <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${collapsed ? '' : 'rotate-180'}`} />
            </span>
          </div>
        </div>

        {/* Progress bar (always visible, including the collapsed compact view) */}
        <div className="h-1.5 rounded-full bg-border overflow-hidden">
          <div
            className="h-full rounded-full bg-[hsl(var(--success))] transition-all duration-500"
            style={{ width: `${allSteps.length ? Math.round((completedCount / allSteps.length) * 100) : 0}%` }}
          />
        </div>
        </div>

        {/* Steps — collapsible on mobile via grid-rows 0fr↔1fr (no hardcoded height); always
            open on desktop. The top spacing lives inside so it collapses with the steps. */}
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none md:grid-rows-[1fr] ${
            collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
          }`}
        >
          <div className="overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 pt-3">
              {allSteps.map((step, index) => (
                <StepCard
                  key={step.id}
                  step={step}
                  stepNumber={index + 1}
                  isNext={nextStep?.id === step.id}
                  onClick={() => onStepClick(step.id)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Condensed sticky bar (desktop only) — pinned under the header after the full banner
          scrolls away. On mobile the collapsible banner replaces it. */}
      {condensedEnabled && showCondensed && nextStep && (
        <div
          ref={condensedRef}
          className="fixed inset-x-0 z-40 border-b border-border bg-secondary shadow-sm"
          style={{ top: headerH }}
        >
          <div className={CONTENT_WIDTH}>
            <div className="flex items-center gap-3 py-2.5">
              <ProgressRing value={completedCount} total={allSteps.length} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground leading-tight truncate">Get ready to invest</p>
                <p className="text-xs text-muted-foreground leading-tight truncate">
                  {completedCount} of {allSteps.length} complete
                </p>
              </div>
              <button
                type="button"
                onClick={() => nextStep.isAccessible && onStepClick(nextStep.id)}
                disabled={!nextStep.isAccessible}
                className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3.5 py-2 text-xs font-semibold text-background hover:brightness-90 transition-colors whitespace-nowrap flex-shrink-0 disabled:opacity-50"
              >
                {nextStep.title}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }
);

/**
 * Individual step card component.
 * Memoized to prevent re-renders when sibling steps update.
 */
const StepCard = React.memo<{
  step: OnboardingStep;
  stepNumber: number;
  isNext: boolean;
  onClick: () => void;
}>(function StepCard({ step, stepNumber, isNext, onClick }) {
  const StepIcon = STEP_ICONS[step.id] ?? Circle;
  const isCompleted = step.completed;
  const isLocked = !step.isAccessible;
  // A completed step is done — clicking it should be a no-op. Only accessible, not-yet-
  // complete steps are actionable.
  const isClickable = step.isAccessible && !isCompleted;

  return (
    <button
      onClick={isClickable ? onClick : undefined}
      disabled={!step.isAccessible}
      aria-disabled={!isClickable}
      className={`
        p-2.5 rounded-xl border text-left transition-all flex items-center gap-3 h-full
        ${
          isCompleted
            ? // Done → check stays bold, text greys out (below) so attention goes to what's left.
              'border-border bg-card cursor-default'
            : isNext
            ? // The single actionable "next" step → emphasized.
              'border-foreground bg-card shadow-sm hover:bg-surface-subtle'
            : isLocked
            ? 'border-border bg-surface-subtle cursor-not-allowed'
            : // Accessible but not the immediate next → neutral.
              'border-border bg-card hover:bg-surface-subtle'
        }
      `}
    >
      {/* Icon — FILLED for done/next, OUTLINED for to-do/locked (a universal done cue). */}
      <div
        className={`
          w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2
          ${
            isCompleted
              ? 'bg-[hsl(var(--success))] border-transparent'
              : isNext
              ? 'bg-primary border-transparent'
              : 'bg-transparent border-border'
          }
        `}
      >
        {isCompleted ? (
          <Check className="w-4 h-4 text-background" aria-hidden="true" />
        ) : isNext ? (
          <StepIcon className="w-4 h-4 text-primary-foreground" aria-hidden="true" />
        ) : isLocked ? (
          <Lock className="w-3.5 h-3.5 text-muted-foreground/70" aria-hidden="true" />
        ) : (
          <StepIcon className="w-4 h-4 text-muted-foreground/70" aria-hidden="true" />
        )}
      </div>

      {/* "Next" pill on the actionable step, otherwise the step number + title */}
      <div className="min-w-0">
        {isNext ? (
          <span className="inline-block rounded-full bg-foreground px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-background mb-0.5">
            Next
          </span>
        ) : (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Step {stepNumber}
          </p>
        )}
        <div className={`text-sm font-medium ${isCompleted ? 'text-muted-foreground/70' : isLocked ? 'text-muted-foreground' : 'text-foreground'}`}>
          {step.title}
        </div>
      </div>
    </button>
  );
});

/**
 * Loading skeleton shown while fetching onboarding state
 */
function LoadingSkeleton() {
  return (
    <div className="bg-secondary border border-border rounded-2xl p-5 mb-6 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="h-6 bg-border rounded w-48 mb-2" />
          <div className="h-4 bg-border rounded w-64" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="border border-border bg-card rounded-xl p-3">
            <div className="flex items-start gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-border" />
            </div>
            <div className="h-4 bg-border rounded w-24 mb-1" />
            <div className="h-3 bg-border rounded w-32" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Error banner shown when onboarding fetch fails
 */
function ErrorBanner({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="bg-destructive/10 border-2 border-destructive/30 rounded-2xl p-5 mb-6">
      <div className="flex items-start gap-4">
        <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-lg mb-1 text-destructive">
            Unable to load onboarding progress
          </h3>
          <p className="text-sm text-muted-foreground mb-3">{message}</p>
          <div className="flex gap-2">
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm">
                Try Again
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Fallback function to format step IDs into readable titles.
 * In production, backend should always provide titles.
 */
function formatStepTitle(stepId: string): string {
  return stepId
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Small circular progress ring used in the condensed sticky bar.
 */
function ProgressRing({ value, total }: { value: number; total: number }) {
  const pct = total > 0 ? value / total : 0;
  const r = 13;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative flex-shrink-0" style={{ width: 34, height: 34 }}>
      <svg width="34" height="34" viewBox="0 0 34 34" className="-rotate-90">
        <circle cx="17" cy="17" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="3" />
        <circle
          cx="17"
          cy="17"
          r={r}
          fill="none"
          stroke="hsl(var(--success))"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: 'stroke-dashoffset 0.4s ease' }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-foreground">
        {value}/{total}
      </span>
    </div>
  );
}
